import { zodResolver } from "@hookform/resolvers/zod";
import { extrudeAdditionalInfo, extrudeKeyInfo, isWeb } from "@stepapp/core";
import { ApartmentType } from "@stepapp/core/apiTypes";
import { palette } from "@stepapp/ui";
import { Slot, Stack, useGlobalSearchParams } from "expo-router";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useAppSelector } from "../../../src/hooks";
import { useHomeEditSchema } from "../../../src/schemas";

const MyHomeEditLayout = () => {
  const { propertyId = "" } = useGlobalSearchParams<{ propertyId: string }>();
  const { t } = useTranslation();

  const properties = useAppSelector(({ user }) => user.properties);
  const floorArea = useAppSelector(({ homeProfile }) => homeProfile.floorArea);
  const city = useAppSelector(({ appConfig }) => appConfig.city);

  const property = properties?.find((property) => property.propertyId === propertyId);
  const keyInfo = extrudeKeyInfo(property?.notes) || "";

  const homeEditSchema = useHomeEditSchema();

  const defaultValues = useMemo(
    () => ({
      address: {
        city: property?.city || city,
        doorCode: property?.doorCode || "",
        floorArea: property?.floorArea || floorArea || 0,
        postalOrZipCode: property?.postalOrZipCode || "",
        streetAddress1: property?.streetAddress1 || "",
        streetAddress2: property?.streetAddress2 || "",
        name: property?.name || "",
        type: property?.homeType || ApartmentType.Apartment,
        notes: extrudeAdditionalInfo(property?.notes) || "",
      },
      meta: {
        willCustomerBePresent: keyInfo
          ? keyInfo === t("screens.serviceSummary.iWillBeThere")
          : ("deselected" as const),
        keyInfo: keyInfo === t("screens.serviceSummary.iWillBeThere") ? "" : keyInfo,
      },
    }),
    [property, city, floorArea, keyInfo, t],
  );

  const form = useForm<z.infer<typeof homeEditSchema>>({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(homeEditSchema),
    defaultValues,
  });

  if (isWeb) {
    return (
      <FormProvider {...form}>
        <Slot />
      </FormProvider>
    );
  }

  return (
    <FormProvider {...form}>
      <Stack
        screenOptions={{
          headerShown: false,
          contentStyle: { backgroundColor: palette.common.white },
        }}
      >
        <Stack.Screen
          name="index"
          options={{
            headerShown: false,
          }}
        />
        <Stack.Screen
          name="address-autocomplete"
          options={{
            animation: "slide_from_bottom",
            presentation: "modal",
          }}
        />
      </Stack>
    </FormProvider>
  );
};

export default MyHomeEditLayout;
